import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contextos/AuthContext';
import { Button, Icon, Input } from '../../../ui';
import AuthLayout from '../AuthLayout';
import styles from './DefinirSenha.module.css';

type Erros = {
  senha?: string;
  confirmacao?: string;
};

/**
 * Página do link enviado por e-mail (/definir-senha?token=...). Usada tanto pela
 * migração (usuário sem senha vindo do Firebase/Google) quanto por "esqueci a senha".
 */
export default function DefinirSenha() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const { definirSenha, processando } = useAuth();

  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erros, setErros] = useState<Erros>({});
  const [erroGeral, setErroGeral] = useState('');

  const submeter = async () => {
    if (processando) return;
    setErroGeral('');

    const novos: Erros = {};
    if (senha.length < 8) novos.senha = 'A senha precisa ter pelo menos 8 caracteres.';
    if (!novos.senha && confirmacao !== senha) novos.confirmacao = 'As senhas não coincidem.';
    setErros(novos);
    if (Object.keys(novos).length > 0) return;

    const resultado = await definirSenha(token, senha);
    if (resultado.ok) return; // o AuthContext já aplica a sessão e navega para /dashboard
    setErroGeral(resultado.mensagem || 'Não foi possível definir a senha. O link pode ter expirado.');
  };

  // Link aberto sem token: orienta a pedir um novo em vez de mostrar um formulário inútil.
  if (!token) {
    return (
      <AuthLayout titulo="Link inválido" subtitulo="Este endereço está incompleto ou já não funciona mais.">
        <div className={styles.painel}>
          <span className={styles.painelIcone}>
            <Icon nome="link_off" tamanho={22} cor="var(--error)" />
          </span>
          <p className={styles.painelTexto}>
            O link que você abriu não tem um código válido. Isso acontece quando o endereço é copiado pela metade ou o
            link já expirou. Solicite um novo e-mail para continuar.
          </p>
          <Link to="/recuperar-senha" className={styles.linkBotao}>
            Solicitar novo link
          </Link>
          <p className={styles.trocaFluxo}>
            Lembrou da senha?{' '}
            <Link to="/entrar" className={styles.link}>
              Entrar
            </Link>
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout titulo="Defina sua senha" subtitulo="Escolha uma senha nova e você já entra direto na sua conta.">
      <div className={styles.form}>
        {erroGeral && (
          <div className={styles.alerta} role="alert">
            <Icon nome="error" tamanho={18} cor="var(--error)" />
            <span className={styles.alertaTexto}>
              {erroGeral}{' '}
              <Link to="/recuperar-senha" className={styles.link}>
                Solicitar novo link
              </Link>
            </span>
          </div>
        )}

        <Input
          label="Nova senha"
          tipo="password"
          icone="lock"
          valor={senha}
          onChange={setSenha}
          placeholder="Crie uma senha"
          erro={erros.senha}
          dica="Pelo menos 8 caracteres."
          obrigatorio
          autoFocus
          onEnter={submeter}
        />
        <Input
          label="Confirme a senha"
          tipo="password"
          icone="lock_reset"
          valor={confirmacao}
          onChange={setConfirmacao}
          placeholder="Repita a senha"
          erro={erros.confirmacao}
          obrigatorio
          onEnter={submeter}
        />

        <Button tamanho="lg" icone="key" carregando={processando} onClick={submeter}>
          Definir senha e entrar
        </Button>
      </div>
    </AuthLayout>
  );
}
